import React from 'react'
import compact from 'lodash/compact'
import keyBy from 'lodash/keyBy'
import map from 'lodash/map'
import sortBy from 'lodash/sortBy'
import uniq from 'lodash/uniq'
import { Text, Flex, Box } from '@fundamentals'
import { EastOutlined } from '@mui/icons-material'
import { TimelineBlockBase } from './TimelineBlockBase'
import {
  OnboardingFormAuditData,
  OnboardingFormDataEntry,
} from '@core/entities/audit/AuditResult/IAuditResultMetadata'
import { localizedStrings } from '@core/strings'

type TimelineOnboardingFormDiffBlockProps = {
  preFormData?: OnboardingFormAuditData
  postFormData?: OnboardingFormAuditData
}

const formatValue = (entry?: OnboardingFormDataEntry): string => {
  if (!entry) return localizedStrings.none
  if (entry.type === 'STRING') {
    const trimmed = entry.textValue?.trim() ?? ''
    return trimmed.length > 0 ? trimmed : localizedStrings.none
  }
  const labels = map(entry.selectedOptions, 'label')
  return labels.length > 0 ? labels.join(', ') : localizedStrings.none
}

const sortedOptionIds = (entry?: OnboardingFormDataEntry): string =>
  entry?.type === 'SELECT'
    ? sortBy(map(entry.selectedOptions, 'uuid')).join(',')
    : ''

const textValue = (entry?: OnboardingFormDataEntry): string =>
  entry?.type === 'STRING' ? entry.textValue?.trim() ?? '' : ''

const isSameValue = (
  pre?: OnboardingFormDataEntry,
  post?: OnboardingFormDataEntry,
): boolean => {
  const type = pre?.type ?? post?.type
  return type === 'STRING'
    ? textValue(pre) === textValue(post)
    : sortedOptionIds(pre) === sortedOptionIds(post)
}

export const TimelineOnboardingFormDiffBlock: React.FC<
  TimelineOnboardingFormDiffBlockProps
> = ({ preFormData, postFormData }) => {
  const preEntries = preFormData?.data ?? []
  const postEntries = postFormData?.data ?? []

  if (preEntries.length === 0 && postEntries.length === 0) return null

  const preByPropertyId = keyBy(preEntries, 'propertyId')
  const postByPropertyId = keyBy(postEntries, 'propertyId')
  const propertyIds = uniq(map([...preEntries, ...postEntries], 'propertyId'))

  const changes = compact(
    propertyIds.map((propertyId) => {
      const pre = preByPropertyId[propertyId]
      const post = postByPropertyId[propertyId]
      if (isSameValue(pre, post)) return null
      return {
        propertyId,
        name: pre?.name ?? post?.name ?? propertyId,
        pre,
        post,
      }
    }),
  )

  if (changes.length === 0) {
    return (
      <TimelineBlockBase title={localizedStrings.onboardingForm}>
        <Text variant='body2' color='text.secondary'>
          {localizedStrings.noChangesDetected}
        </Text>
      </TimelineBlockBase>
    )
  }

  return (
    <TimelineBlockBase title={localizedStrings.onboardingForm}>
      {changes.map((change) => (
        <Box key={change.propertyId} mb={1.5}>
          <Text variant='body2' fontWeight='bold' mb={0.5}>
            {change.name}
          </Text>
          <Flex alignItems='flex-start'>
            <Text variant='body2' color='text.primary' sx={{ maxWidth: '40%' }}>
              {formatValue(change.pre)}
            </Text>
            <EastOutlined sx={{ color: 'text.secondary', mx: 1 }} />
            <Text variant='body2' color='text.primary' sx={{ maxWidth: '40%' }}>
              {formatValue(change.post)}
            </Text>
          </Flex>
        </Box>
      ))}
    </TimelineBlockBase>
  )
}
